import { BehavioralMentalStatus } from "@/features/note-content/components/BehavioralMentalStatus";
import { BehavioralSymptomTrend } from "@/features/note-content/components/BehavioralSymptomTrend";
import { GenericMultiselect } from "@/features/note-content/components/GenericMultiselect";
import { makeCnaiProps, makeCnaiTemplateProps } from "@/lib/helpers/clinically-ai";

import { DischargeContent } from "./(discharge)/DischargeContent";
import { GenericSectionNotes } from "./components/GenericSectionNotes";
import MemberProfileNotesDuration from "./components/MemberProfileNotesDuration";
import { NoteGoalsAndObjectiveDisplay } from "./components/NoteGoalsAndObjectiveDisplay";
import { NotesDiagnosis } from "./components/NotesDiagnosis";

export function BehavioralNewNote({
  editable,
  editHandler,
  errors,
  isCallManuallyLinked,
  note,
  patientAnswersEditable,
  profile,
  views,
}) {
  let { createEditView, draftView, profileView } = views;

  if (!editable) {
    patientAnswersEditable = false;
  }
  const content = Object.hasOwn(note, "content") && note.content;

  let behavioralGoals = (
    <NoteGoalsAndObjectiveDisplay
      content={content}
      draftView={draftView}
      editable={editable}
      editHandler={editHandler}
      isCnaiEnabled={true}
      note={note}
      profileView={profileView}
    />
  );

  // see symptomTrendNotesMobile below — same rationale (isCnaiEnabled omitted/false here).
  let behavioralGoalsMobile = (
    <NoteGoalsAndObjectiveDisplay
      content={content}
      draftView={draftView}
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
    />
  );

  let symptomTrendNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="symptom_trend_notes"
      sectionLabel="Symptom Trends/Review"
      textareaProps={makeCnaiProps({
        format: "paragraph",
        id: "symptom-trend-notes",
        instructions: `Write in objective, third-person clinical language. Provide one concise sentence (25-30 words) describing the patient's symptom status and trend since the last visit. Include a brief direct client quote if a relevant one is available in the Transcript. Describe severity, duration, intensity, trend, and functional impact (e.g., sleep, daily activities, work, social engagement). Reference pre-session survey data (PHQ-9, GAD-7) if available. Flag any acute concerns (suicidal ideation, self-harm, homicidal ideation, crisis/ED visits) and note sobriety status with days free of use if substance use is discussed. End with: Severity Rating: X/10 (10 = worst). Example: Clt reports moderate-to-high anxiety increased over past week; sleep disruption, self-criticism, and worry impair functioning. Severity Rating: 7/10`,
        name: "Symptom Trends/Review",
        type: "string",
      })}
    />
  );

  // desktop and mobile render the same field simultaneously (CSS-toggled, not unmounted) —
  // tagging both would give the extension two elements sharing one cnai-template-field-id.
  // Only the desktop copy above carries cnai attrs; this one is DOM-identical but untagged.
  let symptomTrendNotesMobile = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="symptom_trend_notes"
      sectionLabel="Symptom Trends/Review"
    />
  );

  let explorationNotes = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="exploration_notes"
      sectionLabel="Exploration"
      textareaProps={makeCnaiProps({
        format: "paragraph",
        id: "exploration-notes",
        instructions: `Write a 6-11 sentence clinical narrative summarizing the thematic work of the session based on the Transcript. Cover: what the client brought to the session (presenting stressors, concerns, or themes); relevant biographical or contextual detail the client shared that informs the clinical picture (e.g., living situation, relationships, work, health, recent life events); the client's engagement, receptivity, or resistance during the session; any self-identified patterns, insights, or intentions the client expressed; concrete action steps or between-session plans assigned or agreed upon (e.g., homework, behavioral experiments, scheduling strategies, referrals initiated); and the follow-up plan - next session timing/format if stated, or any interim contact plan. Use neutral third-person clinical prose. `,
        name: "Exploration",
        type: "string",
      })}
    />
  );

  // see symptomTrendNotesMobile above — same rationale, kept untagged.
  let explorationNotesMobile = (
    <GenericSectionNotes
      editable={editable}
      editHandler={editHandler}
      note={note}
      profileView={profileView}
      sectionKey="exploration_notes"
      sectionLabel="Exploration"
    />
  );

  let mentalStatus = content.mental_status ? (
    <BehavioralMentalStatus
      createEditView={createEditView}
      editable={editable}
      editHandler={editHandler}
      isCnaiEnabled={true}
      note={note}
      profileView={profileView}
      showTitle={true}
    />
  ) : (
    <></>
  );

  let symptomTrends =
    content.symptom_trends && content.symptom_trends_substances ? (
      <BehavioralSymptomTrend
        editable={editable}
        editHandler={editHandler}
        note={note}
        patientAnswersEditable={patientAnswersEditable}
        profileView={profileView}
      />
    ) : (
      <></>
    );

  let changes = content.changes ? (
    <GenericMultiselect
      className={!profileView ? "grey-container" : ""}
      editable={patientAnswersEditable}
      editHandler={editHandler}
      labelClass="clr-sec"
      note={note}
      profileView={profileView}
      sectionKey="changes"
      sectionLabel="Changes/Updates"
    />
  ) : (
    <></>
  );

  let healthCareUse = content.health_care_use ? (
    <GenericMultiselect
      className={!profileView ? "grey-container" : ""}
      editable={patientAnswersEditable}
      editHandler={editHandler}
      labelClass="clr-sec"
      note={note}
      profileView={profileView}
      sectionKey="health_care_use"
      sectionLabel="Health Care Use"
    />
  ) : (
    <></>
  );

  let discharges = Object.hasOwn(content, "discharges") ? (
    <DischargeContent
      classes={{
        card: editable ? "CardSeconday" : null,
        colEditable: "col-12",
        colReadonly: "col-5",
      }}
      dischargeValues={note.content.discharges}
      editable={editable}
      setDischargeValues={(val, key, nested) => editHandler("discharges", val, nested)}
    />
  ) : null;

  let diagnosis = (
    <NotesDiagnosis
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profile={profile}
      profileView={profileView}
    />
  );

  let duration = !isCallManuallyLinked ? (
    <MemberProfileNotesDuration
      editable={editable}
      editHandler={editHandler}
      errors={errors}
      note={note}
      profileView={profileView}
    />
  ) : (
    <></>
  );

  let bottomArea;

  if (createEditView) {
    bottomArea = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  } else if (profileView) {
    bottomArea = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  }

  let bottomAreaMobile;

  if (createEditView) {
    bottomAreaMobile = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  } else if (profileView) {
    bottomAreaMobile = (
      <>
        {diagnosis}
        {duration}
      </>
    );
  }

  let otherCss = profileView ? "px-0 mx-0 w-100" : "m-auto";
  let col1Css = profileView ? "col-3" : "col-5";
  let col2Css = profileView ? "col-9 p-0" : "col-7 pl-3 pr-0 tw:flex tw:flex-col tw:gap-5";

  let desktopContent = (
    <div className="tw:max-md:hidden tw:md:flex tw:md:flex-col tw:md:gap-5">
      <div>{behavioralGoals}</div>

      <div className={`row ${otherCss}`}>
        <aside className={`${col1Css} tw:flex tw:flex-col tw:gap-5 tw:p-0!`}>
          <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
            {symptomTrendNotes}
          </div>

          <div className={profileView ? "p-3 border-right-note border-bottom-note" : ""}>
            {explorationNotes}
          </div>

          <div className={profileView ? "p-3 border-right-note" : ""}>{mentalStatus}</div>
        </aside>

        <div className={`${col2Css}`}>
          <div className={profileView ? "p-3" : ""}>{symptomTrends}</div>
          <div className={profileView ? "p-3 border-top-note" : ""}>{changes}</div>
          <div className={profileView ? "p-3 border-top-note" : ""}>{healthCareUse}</div>

          {discharges && (
            <div className={profileView ? "p-3 border-top-note" : ""}>{discharges}</div>
          )}

          <div className={profileView ? "p-3" : "tw:contents"}>{bottomArea}</div>
        </div>
      </div>
    </div>
  );

  let mobileContent = (
    <div className="d-block d-md-none d-lg-none">
      {behavioralGoalsMobile}
      {symptomTrends}
      {symptomTrendNotesMobile}
      {explorationNotesMobile}
      {changes}
      {healthCareUse}
      {discharges}
      {bottomAreaMobile}
    </div>
  );

  return (
    <div
      className="tw:contents"
      {...makeCnaiTemplateProps({
        id: "behavioral-progress-note",
        name: "Behavioral Health Progress Note",
        type: "Individual Progress Note",
      })}
    >
      {desktopContent}
      {mobileContent}
    </div>
  );
}
